import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Briefcase,
  User,
  Users,
  Calendar,
  Globe,
  Clock,
  ShieldCheck,
  Mic,
  Voicemail,
  Smartphone,
  IdCard,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { useUserProfile } from '../../hooks/useUserProfile.js'
import { useUserType } from '../../hooks/useUserType.js'

function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) {
      const first = parts[0]?.[0] ?? ''
      const second = parts[1]?.[0] ?? ''
      return (first + second).toUpperCase()
    }
    if (parts.length === 1) {
      return (parts[0] ?? email).slice(0, 2).toUpperCase()
    }
  }
  return email.slice(0, 2).toUpperCase()
}

function getAvatarColor(email: string): string {
  const colors = [
    'bg-[#3366FF]',
    'bg-emerald-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-violet-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-indigo-500',
  ]
  let hash = 0
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length] ?? 'bg-[#3366FF]'
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string | null | undefined
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-slate-50 text-slate-400">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <p className="truncate text-sm font-medium text-slate-800">{value || '—'}</p>
      </div>
    </div>
  )
}

function BoolRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: boolean | null | undefined
}): JSX.Element {
  const enabled = value === true
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-slate-50 text-slate-400">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <div className="flex items-center gap-1.5">
          {enabled ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <XCircle className="h-4 w-4 text-slate-300" />
          )}
          <span className={`text-sm font-medium ${enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
            {enabled ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  )
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}): JSX.Element {
  return (
    <section className="rounded-[3px] border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3">
        <span className="text-[#3366FF]">{icon}</span>
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {children}
    </section>
  )
}

function StatusBadge({ status }: { status: string | null }): JSX.Element {
  if (!status) return <span className="text-slate-400">—</span>
  const isActive = status === 'active'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

export default function UserProfilePage(): JSX.Element {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { profile, loading, error } = useUserProfile(userId)
  const { showSoftphone } = useUserType()

  if (loading) {
    return (
      <div className="min-h-full bg-[#F8F9FB] p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-32 bg-slate-200 rounded" />
          <div className="h-40 bg-white border border-slate-200 rounded-[3px]" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="h-64 bg-white border border-slate-200 rounded-[3px]" />
            <div className="h-64 bg-white border border-slate-200 rounded-[3px]" />
            <div className="h-64 bg-white border border-slate-200 rounded-[3px]" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-full bg-[#F8F9FB] p-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3366FF] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Directory
        </button>
        <div className="mt-8 rounded-[3px] border border-rose-200 bg-rose-50 p-8 text-center">
          <p className="text-sm font-medium text-rose-700">{error ?? 'User not found'}</p>
        </div>
      </div>
    )
  }

  const displayName = profile.name ?? profile.email
  const avatarColor = getAvatarColor(profile.email)

  return (
    <div className="min-h-full bg-[#F8F9FB] p-6">
      {/* Back link — navigates back without opening a new tab */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3366FF] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Directory
        </button>
      </div>

      {/* Header card — full width */}
      <section className="rounded-[3px] border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-6">
          <div
            className={`flex h-24 w-24 shrink-0 items-center justify-center rounded-full ${avatarColor} text-white text-2xl font-bold`}
          >
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={displayName}
                className="h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <span>{getInitials(profile.name, profile.email)}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{displayName}</h1>
              <StatusBadge status={profile.status} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Mail className="h-4 w-4" />
                {profile.email}
              </span>
              {profile.loginid && (
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  {profile.loginid}
                </span>
              )}
              {profile.extension && showSoftphone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  <span className="font-mono font-semibold text-[#3366FF]">{profile.extension}</span>
                </span>
              )}
              {profile.mobileNumber && (
                <span className="flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4" />
                  {profile.mobileNumber}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {profile.department && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  <Briefcase className="h-3 w-3" />
                  {profile.department}
                </span>
              )}
              {profile.branch && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700">
                  <Building2 className="h-3 w-3" />
                  {profile.branch}
                </span>
              )}
              {profile.location && (
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
                  <MapPin className="h-3 w-3" />
                  {profile.location}
                </span>
              )}
              {profile.userType && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  <Users className="h-3 w-3" />
                  {profile.userType === 'pbx' ? 'PBX' : 'Call Center'}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Detail sections — full width 3-column grid */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Personal Information */}
        <SectionCard title="Personal Information" icon={<User className="h-4 w-4" />}>
          <InfoRow icon={<User className="h-4 w-4" />} label="First Name" value={profile.firstName} />
          <InfoRow icon={<User className="h-4 w-4" />} label="Last Name" value={profile.lastName} />
          <InfoRow icon={<IdCard className="h-4 w-4" />} label="Employee ID" value={profile.employeeId} />
          <InfoRow icon={<User className="h-4 w-4" />} label="Gender" value={profile.sex} />
          <InfoRow icon={<Smartphone className="h-4 w-4" />} label="Mobile Number" value={profile.mobileNumber} />
          <InfoRow icon={<Calendar className="h-4 w-4" />} label="Date of Birth" value={formatDate(profile.dateOfBirth)} />
        </SectionCard>

        {/* Office Information */}
        <SectionCard title="Office Information" icon={<Building2 className="h-4 w-4" />}>
          <InfoRow icon={<MapPin className="h-4 w-4" />} label="Location" value={profile.location} />
          <InfoRow icon={<Building2 className="h-4 w-4" />} label="Branch" value={profile.branch} />
          <InfoRow icon={<Briefcase className="h-4 w-4" />} label="Department" value={profile.department} />
          <InfoRow icon={<Globe className="h-4 w-4" />} label="Country" value={profile.country} />
          <InfoRow icon={<MapPin className="h-4 w-4" />} label="Address" value={profile.address} />
          <InfoRow icon={<Clock className="h-4 w-4" />} label="Timezone" value={profile.timezone} />
          <InfoRow icon={<Calendar className="h-4 w-4" />} label="Date of Joining" value={formatDate(profile.dateOfJoining)} />
        </SectionCard>

        {/* Telephony Configuration */}
        {showSoftphone && (
          <SectionCard title="Telephony Configuration" icon={<Phone className="h-4 w-4" />}>
            <InfoRow icon={<Phone className="h-4 w-4" />} label="Extension" value={profile.extension} />
            <InfoRow icon={<User className="h-4 w-4" />} label="Caller ID Name" value={profile.callerIdName} />
            <InfoRow icon={<Phone className="h-4 w-4" />} label="Caller ID Number" value={profile.callerIdNumber} />
            <InfoRow icon={<Phone className="h-4 w-4" />} label="Max Concurrent Calls" value={profile.maxConcurrentCalls?.toString()} />
            <BoolRow icon={<Phone className="h-4 w-4" />} label="Allow Outbound Calling" value={profile.allowOutboundCalling} />
            <BoolRow icon={<Phone className="h-4 w-4" />} label="Allow Extension Calling" value={profile.allowExtensionCalling} />
            <BoolRow icon={<Mic className="h-4 w-4" />} label="Recording Enabled" value={profile.enableRecording} />
            <BoolRow icon={<Voicemail className="h-4 w-4" />} label="Voicemail Enabled" value={profile.enableVoicemail} />
          </SectionCard>
        )}

        {/* Account Information — spans full width on large screens */}
        <SectionCard title="Account Information" icon={<ShieldCheck className="h-4 w-4" />}>
          <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={profile.email} />
          <InfoRow icon={<User className="h-4 w-4" />} label="Login ID" value={profile.loginid} />
          <InfoRow icon={<Users className="h-4 w-4" />} label="User Type" value={profile.userType === 'pbx' ? 'PBX' : profile.userType === 'callcenter' ? 'Call Center' : profile.userType} />
          <div className="flex items-center gap-3 py-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-slate-50 text-slate-400">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Status</p>
              <div className="mt-0.5">
                <StatusBadge status={profile.status} />
              </div>
            </div>
          </div>
          <InfoRow icon={<Calendar className="h-4 w-4" />} label="Member Since" value={formatDate(profile.createdAt)} />
        </SectionCard>
      </div>
    </div>
  )
}
